import { inject, Injectable } from '@angular/core';
import { signalStore, withState, withMethods, } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, map, of, pipe, switchMap, tap } from 'rxjs';
import { ELoadingEntity } from 'src/models/generic-store.model';
import { withDevtools, updateState } from '@angular-architects/ngrx-toolkit';
import { RestApiService } from 'src/services/rest-api.service';
import { IPageRequest } from 'src/models/common.model';
import { IBlogState } from 'src/models/blog.model';
import { withError, withLoading, withoutError, withoutLoading, withRequestStatus } from '../store-features/with-request-status.store-feature';

enum EBlogActions {
  ARTICLES_PAGE_REQUEST = '[Blog] Articles Page Request',
  ARTICLES_PAGE_SUCCESS = '[Blog] Articles Page Success',
  ARTICLES_PAGE_FAILURE = '[Blog] Articles Page Failure',
  ARTICLE_DETAIL_REQUEST = '[Blog] Article Detail Request',
  ARTICLE_DETAIL_SUCCESS = '[Blog] Article Detail Success',
  ARTICLE_DETAIL_FAILURE = '[Blog] Article Detail Failure',
  ARTICLE_PREVIEW_REQUEST = '[Blog] Article Preview Request',
  ARTICLE_PREVIEW_SUCCESS = '[Blog] Article Preview Success',
  ARTICLE_PREVIEW_FAILURE = '[Blog] Article Preview Failure',
  RESET_STATE = '[Blog] Reset State',
}

const initialBlogState: IBlogState = {
  articlesPage: null,
  articleDetail: null,
};

@Injectable({ providedIn: 'root' })
export class BlogStore extends signalStore(
  withDevtools('blog'),
  withState(initialBlogState),
  withRequestStatus(),
  withMethods((store, restApiService = inject(RestApiService)) => {

    const getArticlesPage = rxMethod<{ pageRequest: IPageRequest; search: string | null }>(
      pipe(
        tap(() =>
          updateState(store, EBlogActions.ARTICLES_PAGE_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.BLOG_ARTICLES_PAGE, loadingProps),
          }))
        ),
        switchMap(({ pageRequest, search }) =>
          restApiService.getBlogArticlesPage(pageRequest, search).pipe(
            map(response =>
              updateState(store, EBlogActions.ARTICLES_PAGE_SUCCESS, ({ loadingProps, errorProps }) => ({
                articlesPage: response,
                ...withoutLoading(ELoadingEntity.BLOG_ARTICLES_PAGE, loadingProps),
                ...withoutError(ELoadingEntity.BLOG_ARTICLES_PAGE, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EBlogActions.ARTICLES_PAGE_FAILURE, ({ loadingProps, errorProps }) => ({
                  ...withoutLoading(ELoadingEntity.BLOG_ARTICLES_PAGE, loadingProps),
                  ...withError(ELoadingEntity.BLOG_ARTICLES_PAGE, { error, errorParams: { pageRequest } }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const getArticleDetail = rxMethod<{ articleId: number }>(
      pipe(
        tap(() =>
          updateState(store, EBlogActions.ARTICLE_DETAIL_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.BLOG_ARTICLE_DETAIL, loadingProps),
          }))
        ),
        switchMap(({ articleId }) =>
          restApiService.getBlogArticleDetail(articleId).pipe(
            map(response =>
              updateState(store, EBlogActions.ARTICLE_DETAIL_SUCCESS, ({ loadingProps, errorProps }) => ({
                articleDetail: response,
                ...withoutLoading(ELoadingEntity.BLOG_ARTICLE_DETAIL, loadingProps),
                ...withoutError(ELoadingEntity.BLOG_ARTICLE_DETAIL, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EBlogActions.ARTICLE_DETAIL_FAILURE, ({ loadingProps, errorProps }) => ({
                  ...withoutLoading(ELoadingEntity.BLOG_ARTICLE_DETAIL, loadingProps),
                  ...withError(ELoadingEntity.BLOG_ARTICLE_DETAIL, { error, errorParams: { articleId } }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const getArticlePreview = rxMethod<{ token: string }>(
      pipe(
        tap(() =>
          updateState(store, EBlogActions.ARTICLE_PREVIEW_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.BLOG_ARTICLE_PREVIEW, loadingProps),
          }))
        ),
        switchMap(({ token }) =>
          restApiService.getBlogArticlePreview(token).pipe(
            map(response =>
              updateState(store, EBlogActions.ARTICLE_PREVIEW_SUCCESS, ({ loadingProps, errorProps }) => ({
                articleDetail: response,
                ...withoutLoading(ELoadingEntity.BLOG_ARTICLE_PREVIEW, loadingProps),
                ...withoutError(ELoadingEntity.BLOG_ARTICLE_PREVIEW, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EBlogActions.ARTICLE_PREVIEW_FAILURE, ({ loadingProps, errorProps }) => ({
                  ...withoutLoading(ELoadingEntity.BLOG_ARTICLE_PREVIEW, loadingProps),
                  ...withError(ELoadingEntity.BLOG_ARTICLE_PREVIEW, { error, errorParams: { token } }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const resetState = () => {
      updateState(store, EBlogActions.RESET_STATE, { ...initialBlogState });
    }

    return {
      getArticlesPage,
      getArticleDetail,
      getArticlePreview,
      resetState,
    };
  })
) {}
